import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { Observable, of, Subject } from "rxjs";
import { first } from "rxjs/operators";
import { ContactFormComponent } from "src/app/core/survey/shared/components/contact-form/contact-form.component";
import { Address } from "src/app/shared/Models/Address";
import { Building, Person } from "src/app/shared/Models/read";
import { OutsideSurveyService } from "src/app/shared/services/outside-survey.service";
import { ToastService } from "src/app/shared/toast.service";
import { SurveyFrameTabTemplateComponent } from "../../../../../shared/survey-frame-list/survey-frame-tab-template/survey-frame-tab-template.component";

@Component({
  selector: "app-inside-survey-contact-info-form",
  templateUrl: "./inside-survey-contact-info-form.component.html",
  styleUrls: ["./inside-survey-contact-info-form.component.scss"],
})
export class InsideSurveyContactInfoFormComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() isAuthCanWrite: boolean;
  @ViewChild("contactRef")
  contactComp: ContactFormComponent;
  @Input() building: Building;
  @Input() buildingId: string;
  @Input() address: Address;
  @Input() index: number;
  @Input() buildingIndex: number;
  @Output() updateContact = new EventEmitter<any>();

  @ViewChild("tabs", { static: true })
  tabComponent: SurveyFrameTabTemplateComponent;

  contacts: Person[];
  selectedContact: Person;
  addNewContactScreenActive: boolean;
  selectedSearchResult: boolean;

  contexts: any[] = [];

  get showTabContent() {
    return (
      (!!this.tabComponent.openTab &&
        this.tabComponent.openTab != "new-contact") ||
      (this.addNewContactScreenActive && !!this.selectedContact)
    );
  }

  constructor(
    private outsideSurveyService: OutsideSurveyService,
    private toastService: ToastService
  ) {}

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit() {
    this.contacts = this.building.persons ? this.building.persons : [];
    this.updateContexts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.building) {
      this.contacts = this.building.persons ? this.building.persons : [];
      this.updateContexts();
    }
  }

  toggleNewContact(bool?: boolean) {
    let i = this.contexts.findIndex((c) => c.id == "new-contact");
    let addNewContact = () => {
      this.addNewContactScreenActive = true;
      this.contexts.unshift({
        id: "new-contact",
        disabled: true,
        hidden: false,
        contact: new Person(),
        noContacts: false,
        newContact: true,
      });
      this.tabComponent.onToggleDetail("new-contact", true);
    };
    let removeNewContact = () => {
      this.addNewContactScreenActive = false;
      this.contexts.splice(i, 1);
      this.tabComponent.onToggleDetail(undefined, true);
    };
    if (bool == undefined)
      if (i == -1) addNewContact();
      else removeNewContact();
    else if (bool && i == -1) addNewContact();
    else if (i != -1) removeNewContact();
  }

  updateContexts() {
    this.addNewContactScreenActive = false;
    this.contexts =
      this.contacts?.map((c) => ({
        id: c.id,
        disabled: false,
        hidden: false,
        contact: c,
        noContacts: false,
        newContact: false,
      })) ?? [];
    let i = this.contexts.findIndex((c) => c.id == "no-contact");
    if (this.contacts.length == 0 && i == -1)
      this.contexts.unshift({
        id: "no-contacts",
        disabled: true,
        hidden: this.contacts?.length > 0,
        contact: undefined,
        noContacts: true,
        newContact: false,
      });
    else if (i != -1) this.contexts.splice(i, 1);
    this.tabComponent.onToggleDetail(undefined);
  }

  onRemoveFromListContact(contact) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    this.outsideSurveyService
      .removeContactFromList({
        buildingId: this.buildingId,
        personId: contact.id,
      })
      .pipe(first())
      .subscribe((res) => {
        this.selectedContact = null;
        let i = this.contacts.findIndex((c) => c.id == contact.id);
        if (i != -1) this.contacts.splice(i, 1);
        this.updateContexts();
      });
  }

  onEditContact(contact) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }
    let i = this.contacts.findIndex((c) => c.id == contact.id);
    if (i != -1) this.contacts[i] = contact;
    this.updateContexts();

    this.updateContact.emit(contact);
  }

  onAddNewContactToList(contact) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }
    if (contact) {
      const exists = this.contacts.some((c) => c.id === contact.id);
      if (!exists) this.contacts.push(contact);
      this.updateContexts();
      this.tabComponent.onToggleDetail(contact.id);
    }
    this.cancelNewContact();
  }

  onSelectContact(contact) {
    this.toggleNewContact(false);
    if (this.selectedContact === contact) this.selectedContact = null;
    else this.selectedContact = contact;
  }

  onAddContactBtn() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    this.selectedContact = null;
    this.toggleNewContact(true);
    this.tabComponent.onToggleDetail("new-contact");
  }

  onSaveContactToList() {
    if (this.selectedContact.id)
      this.outsideSurveyService
        .addContactToList({
          buildingId: this.buildingId,
          personId: this.selectedContact.id,
        })
        .pipe(first())
        .subscribe((res) => {
          this.selectedContact.buildingId = this.buildingId;
          this.onAddNewContactToList(this.selectedContact);
          this.cancelNewContact();
        });
    else this.contactComp.saveNewContactToList(this.buildingId);
  }

  cancelNewContact() {
    this.addNewContactScreenActive = false;
    this.toggleNewContact(false);
    this.selectedSearchResult = false;
    this.selectedContact = null;
  }

  onSelectSearchResult(searchResult: Person) {
    this.selectedContact = searchResult;
    this.selectedSearchResult = true;
    this.contexts.find((c) => c.id == "new-contact").contact = searchResult;
  }

  onNewContact() {
    this.selectedContact = new Person();
  }

  saveContact() {
    if (this.addNewContactScreenActive)
      if (this.selectedContact.id) this.onSaveContactToList();
      else this.contactComp.saveNewContactToList(this.buildingId);
    else this.contactComp.onSaveContact();
  }

  deleteContact() {
    this.contactComp.onRemoveFromListContact();
  }

  save(): Observable<boolean> {
    return of(true);
  }
}
